// ARP-themed AG Grid defaults. Each host installs them once at boot via AG Grid's global grid
// options, so every grid it renders is on-brand by default — including guests that predate the
// theme. Per-grid props always take precedence, so any of this can still be overridden.

import { createPart, iconOverrides, provideGlobalGridOptions, themeQuartz } from 'ag-grid-community'

import arrowCounterClockwise from '../icons/arrow-counter-clockwise.svg?raw'
import arrowDown from '../icons/arrow-down.svg?raw'
import arrowLeft from '../icons/arrow-left.svg?raw'
import arrowRight from '../icons/arrow-right.svg?raw'
import arrowUp from '../icons/arrow-up.svg?raw'
import arrowsOutCardinal from '../icons/arrows-out-cardinal.svg?raw'
import eyeSlash from '../icons/eye-slash.svg?raw'
import funnelSimple from '../icons/funnel-simple.svg?raw'
import plus from '../icons/plus.regular.svg?raw'
import prohibit from '../icons/prohibit.svg?raw'
import sidebarSimple from '../icons/sidebar-simple.svg?raw'
import stackSimple from '../icons/stack-simple.svg?raw'

// Overrides the Theming API can't express: no theme params cover these, and the rules they
// override sit in AG's core stylesheet, not a removable part.
// Attached via createPart so the rules ship (and scope) with the theme itself rather than a loose global stylesheet.
// Note: AG warns`.ag-*` class names can change across releases, even minor ones — keep these selectors
// minimal and purely presentational.
const agGridThemeManualOverridesPart = createPart({
  feature: 'dossThemeApiGaps',
  css: `
    /* The (⋮) menu button and resize handle reveal on hover only, so headers stay clean at rest. */
    .ag-header-cell .ag-header-cell-menu-button,
    .ag-header-cell .ag-header-cell-resize::after {
      opacity: 0;
      transition: opacity 120ms ease-out;
    }

    .ag-header-cell:hover .ag-header-cell-menu-button,
    .ag-header-cell-menu-button:focus-visible,
    .ag-header-cell:hover .ag-header-cell-resize::after {
      opacity: 1;
    }

    /* Pad the ⋮ into a real 28px small-action box; AG's shadow-spread pad over-rounds its corners. */
    .ag-header-cell .ag-header-cell-menu-button {
      /* Absolutely positioned (AG's relative .ag-header-cell-comp-wrapper, which spans the
       * cell's padded content area, is the containing block) so the hidden button takes no
       * flow space and labels use the full cell width instead of ellipsizing early; the
       * revealed button floats over the label's edge, masked by an opaque chrome backing. */
      position: absolute;
      /* Above the label's sort and filter indicators, which it floats over on hover. */
      z-index: 1;
      inset-inline-end: 0;
      inset-block-start: 50%;
      translate: 0 -50%;
      padding: calc(var(--small-action-padding-inline) - var(--small-action-gap));
      box-shadow: none;
      background-color: var(--shell-background);
    }

    /* Match ag's header button hover state to ARP's ghost button hover state */
    .ag-header-cell .ag-header-cell-menu-button:hover {
      background-image: linear-gradient(var(--ghost-background-hover), var(--ghost-background-hover));
    }

    .ag-header-cell .ag-header-cell-menu-button:active {
      background-image: linear-gradient(var(--ghost-background-active), var(--ghost-background-active));
    }

    /* Right-aligned headers put the sort arrow before the label, but AG still spaces the arrow on
     * its outer side. Move that space between the arrow and the label. */
    .ag-right-aligned-header .ag-sort-indicator-icon {
      padding-inline: 0 var(--ag-spacing);
    }

    /* Pad the filter button into the same small-action box as the ⋮ button, so its hover and
     * active backgrounds get ARP's action radius. AG draws them as a shadow, which rounds the
     * corners further. Unlike the ⋮, it sits beside the label, so it needs no background mask. */
    .ag-header-cell .ag-header-cell-filter-button {
      padding: calc(var(--small-action-padding-inline) - var(--small-action-gap));
      border-radius: var(--action-radius);
    }

    .ag-header-cell .ag-header-cell-filter-button,
    .ag-header-cell .ag-header-cell-filter-button:hover {
      box-shadow: none;
    }

    .ag-header-cell .ag-header-cell-filter-button:active {
      background-color: var(--ghost-background-active);
    }

    /* Show an active filter as the box's background rather than AG's outline */
    .ag-header-cell .ag-header-cell-filter-button.ag-filter-active {
      outline: none;

      /* AG's active-filter dot sits in a gap it cuts from the icon's top corner. Shrink it to suit
       * Phosphor's thinner strokes, and inset it by the padding so it stays on the icon's corner. */
      &::after {
        width: calc(var(--icon-size-small) / 4);
        height: calc(var(--icon-size-small) / 4);
        inset-block-start: calc(var(--small-action-padding-inline) - var(--small-action-gap));
        inset-inline-end: calc(var(--small-action-padding-inline) - var(--small-action-gap));
      }
    }

    /* AG sets cells subpixel-antialiased, which reads heavier than ARP's antialiased body text. */
    .ag-cell {
      -webkit-font-smoothing: antialiased;
    }
    
    /* Disable overscroll bounce on grids */
    .ag-grid-viewport {
      overscroll-behavior: none;
    }

    /* AG's text inputs and pickers use ARP's input ring at rest, hover, error and focus. The focus
     * ring overrides the global focus outline, as in ARP's own inputs. */
    .ag-input-wrapper > :is(.ag-text-field-input, .ag-number-field-input, .ag-date-field-input, .ag-text-area-input),
    .ag-picker-field > .ag-picker-field-wrapper {
      box-shadow: var(--input-shadow-rest);
      transition: box-shadow var(--action-transition-duration) ease;

      &:hover {
        box-shadow: var(--input-shadow-hover);
      }

      &:is(:invalid, .invalid) {
        box-shadow: var(--input-shadow-error);
      }

      &:is(:focus, :focus-within, .ag-picker-has-focus) {
        box-shadow: var(--input-shadow-focus);
        outline: none;
      }
    }
  `,
})

/**
 * The ARP grid theme:
 *   1. Most of the work is done by mapping AG's tokens to ARP semantic tokens
 *   2. Theme parameters are set
 *   3. Custom css parts are added where AG has no native override
 *   4. AG's standard parts removed (ex checkbox) where ARP has default styling.
 * Exposed to guests via `@doss/guest-runtime` so an embed needing bespoke styling can extend this
 * (`dossGridTheme.withParams({...})`) instead of restyling from a stock theme.
 */
export const dossGridTheme = themeQuartz
  .withParams({
    backgroundColor: 'var(--card-background)',
    chromeBackgroundColor: 'var(--shell-background)',
    // No zebra striping — rows are separated by the row hairline only.
    rowHoverColor: 'var(--ghost-background-hover)',
    foregroundColor: 'var(--text)',
    headerTextColor: 'var(--secondary-text)',
    borderColor: 'var(--border-color)',
    // The header column-resize handle uses the translucent hairline border, not a solid accent,
    // so it reads as a light divider.
    headerColumnResizeHandleColor: 'var(--border-color)',
    // --accent is mode-invariant (a warm-black), so selection/active tints use the mode-aware,
    // contrast-clamped focus token instead.
    accentColor: 'var(--focus-border-color)',
    fontFamily: 'var(--font-family-body)',
    fontSize: 'var(--input-text-size)',
    // Strong header weight so the muted-gray header still reads heavier than the near-black body.
    headerFontWeight: 'var(--active-action-font-weight)',
    // Compact header row, aligned with ARP form-control height (32px, density-aware).
    headerHeight: 'var(--form-element-height)',
    borderRadius: 'var(--action-radius)',
    wrapperBorderRadius: 'var(--surface-radius)',
    // Native sub-controls (scrollbars, editors) follow the page color-scheme.
    browserColorScheme: 'inherit',
    // No persistent vertical header separators; the resize affordance is a full-height hairline
    // handle revealed on hover.
    headerColumnBorder: false,
    headerColumnResizeHandleHeight: '100%',
    headerColumnResizeHandleWidth: 'var(--border-width)',
    // Header icon buttons (the ⋮ column menu) styled to match ARP small ghost icon buttons
    iconButtonBackgroundSpread:
      'calc(var(--small-action-padding-inline) - var(--small-action-gap))',
    iconButtonBorderRadius: 'var(--action-radius)',
    iconButtonHoverBackgroundColor: 'var(--ghost-background-hover)',
    iconButtonActiveBackgroundColor: 'var(--ghost-background-active)',
    // Inputs and pickers get ARP's input ring from the CSS overrides above, so AG's borders are off.
    // Their background is set so they keep the input surface on grids with a different background.
    inputBackgroundColor: 'var(--card-background)',
    inputBorder: false,
    inputFocusBorder: false,
    inputInvalidBorder: false,
    inputDisabledBorder: false,
    pickerButtonBorder: false,
    pickerButtonFocusBorder: false,
  })
  .withPart(agGridThemeManualOverridesPart)
  .withPart(
    iconOverrides({
      type: 'image',
      mask: true,
      icons: {
        asc: { svg: arrowUp },
        desc: { svg: arrowDown },
        filter: { svg: funnelSimple },
        grip: { svg: arrowsOutCardinal },
        arrows: { svg: arrowsOutCardinal },
        'not-allowed': { svg: prohibit },
        left: { svg: arrowLeft },
        right: { svg: arrowRight },
        'eye-slash': { svg: eyeSlash },
        pin: { svg: sidebarSimple },
        group: { svg: stackSimple },
        aggregation: { svg: plus },
        pivot: { svg: arrowCounterClockwise },
      },
    }),
  )
  .withoutPart('checkboxStyle')

/**
 * Install the ARP grid defaults for every grid in this realm. Must run before any grid is
 * instantiated. The 'deep' merge strategy keeps per-grid overrides intact: a guest's own props
 * win key-by-key, and object options like `defaultColDef` merge with the guest's values on top.
 */
export const registerDossGridDefaults = (): void => {
  provideGlobalGridOptions(
    {
      theme: dossGridTheme,
      // Sort/filter re-flow is instant — no row-slide animation.
      animateRows: false,
      // Filtering lives in the column (⋮) menu, so headers stay clean at rest — no per-header
      // filter funnel.
      defaultColDef: { suppressHeaderFilterButton: true },
    },
    'deep',
  )
}
